import { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CheckCircle2, AlertTriangle, XCircle, Trophy, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { LunimarRealm } from "@/components/lunimar/lunimarRealms";
import { getTone, type Tone } from "./types";
import SimWalletImpact from "./SimWalletImpact";

interface Props {
  realm?: LunimarRealm;
  /** Semantic tone — drives icon + color. */
  tone: Tone;
  /** Headline result, e.g. "You bought the home." */
  title: string;
  /** One-line plain-English summary of what happened. */
  summary?: string;
  /** Optional wallet delta to render via SimWalletImpact. */
  walletDelta?: number;
  walletPersisted?: boolean;
  /** Optional readiness/risk shift label. */
  shift?: string;
  /** Optional next-step button label. */
  nextLabel?: string;
  onNext?: () => void;
  /** Optional secondary action — usually "Try again". */
  secondaryLabel?: string;
  onSecondary?: () => void;
  children?: ReactNode;
}

const ICONS: Record<Tone, ReactNode> = {
  good: <CheckCircle2 className="h-5 w-5" />,
  caution: <AlertTriangle className="h-5 w-5" />,
  danger: <XCircle className="h-5 w-5" />,
  milestone: <Trophy className="h-5 w-5" />,
  active: <Activity className="h-5 w-5" />,
  neutral: <Activity className="h-5 w-5" />,
};

/**
 * Consequence reveal after a confirmed decision. Always animates in.
 * Carries tone-coded icon, summary, optional wallet impact, and a
 * next-step CTA so each decision has a clear close-loop.
 */
const ConsequencePanel = ({
  realm,
  tone,
  title,
  summary,
  walletDelta,
  walletPersisted = true,
  shift,
  nextLabel,
  onNext,
  secondaryLabel,
  onSecondary,
  children,
}: Props) => {
  const reduce = useReducedMotion();
  const t = getTone(tone, realm);
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: reduce ? 1 : 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: reduce ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-2xl border p-4 backdrop-blur-md sm:p-5"
      style={{
        borderColor: `hsl(${t.border})`,
        background: `linear-gradient(135deg, hsl(${t.bg}), hsl(228 45% 10% / 0.7))`,
        boxShadow: `0 24px 60px -30px hsl(${t.fg} / 0.5)`,
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border"
          style={{
            borderColor: `hsl(${t.fg} / 0.4)`,
            background: `hsl(${t.fg} / 0.14)`,
            color: `hsl(${t.fg})`,
          }}
        >
          {ICONS[tone]}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] font-mono uppercase tracking-widest"
            style={{ color: `hsl(${t.fg})` }}
          >
            Outcome · {t.label}
          </div>
          <h4 className="mt-0.5 font-heading text-base font-bold text-white sm:text-lg">
            {title}
          </h4>
          {summary && (
            <p className="mt-1 text-xs leading-relaxed text-white/80 sm:text-sm">
              {summary}
            </p>
          )}
          {shift && (
            <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-1 font-mono text-[11px] text-white/85">
              {shift}
            </div>
          )}
        </div>
      </div>

      {typeof walletDelta === "number" && walletDelta !== 0 && (
        <div className="mt-3">
          <SimWalletImpact
            realm={realm}
            delta={walletDelta}
            persisted={walletPersisted}
          />
        </div>
      )}

      {children && <div className="mt-3">{children}</div>}

      {(onNext || onSecondary) && (
        <div className="mt-4 flex flex-wrap gap-2">
          {onSecondary && (
            <Button variant="outline" onClick={onSecondary} className="flex-1 sm:flex-none">
              {secondaryLabel ?? "Try again"}
            </Button>
          )}
          {onNext && (
            <Button onClick={onNext} className="flex-1 sm:flex-none">
              {nextLabel ?? "Continue"}
            </Button>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default ConsequencePanel;